import { getAuthToken } from "@/lib/storage"
import { getSocketBaseUrl } from "@/lib/socketBase"
import { io, Socket } from "socket.io-client"
import {
  ArenaCapacityWaitPayload,
  ArenaClaimSlotDto,
  ArenaEnterDto,
  ArenaEnteredPayload,
  ArenaForfeitDto,
  ArenaMatchCardsPayload,
  ArenaMatchEvent,
  ArenaMatchFinishedPayload,
  ArenaMatchFoundPayload,
  ArenaMatchIdDto,
  ArenaMatchSnapshot,
  ArenaOnlineUpdatedPayload,
  ArenaQueuedPayload,
  ArenaSelectCardDto,
  ArenaStatusPayload,
} from "../types/arena"
import {
  BattleCancelledPayload,
  BattleResultPayload,
  BattleStartedPayload,
  CardPlacedPayload,
  ChallengeReceivedPayload,
  ChallengeSendDto,
  ChallengeSentPayload,
  ChallengeStatusPayload,
  DeckBattleCancelledPayload,
  DeckBattleRestoreAck,
  DeckBattleResumePayload,
  DeckBattleStartedPayload,
  DeckChallengeReceivedPayload,
  DeckChallengeSendDto,
  DeckChallengeSentPayload,
  DeckMatchResultPayload,
  DeckOpponentCardReadyPayload,
  DeckOpponentDisconnectedPayload,
  DeckOpponentReconnectedPayload,
  DeckPlayCardWaitingPayload,
  DeckRadarSearchDto,
  DeckRadarStatus,
  DeckRadarStatusRequestDto,
  DeckRoundResultPayload,
  GameBattleConnectedPayload,
  GameBattleErrorPayload,
  OnlineCountPayload,
  OnlinePlayersResponse,
  RadarSearchDto,
  RadarStatus,
} from "../types/battle"

const API_BASE = getSocketBaseUrl()

type BattleEvents = {
  "online-players": () => void
  "challenge-send": (payload: ChallengeSendDto) => void
  "challenge-accept": (payload: { challengeId: string }) => void
  "challenge-decline": (payload: { challengeId: string }) => void
  "challenge-cancel": (payload: { challengeId: string }) => void
  "radar-search": (payload?: RadarSearchDto) => void
  "radar-cancel": () => void
  "radar-status": (payload?: RadarSearchDto) => void
  "qr-create": () => void
  "qr-cancel": () => void
  "qr-join": (payload: { roomCode: string }) => void
  "select-card": (payload: {
    battleId: string
    cardId: string
    abilityId?: string | null
  }) => void
  "deck-radar-search": (payload: DeckRadarSearchDto) => void
  "deck-radar-cancel": () => void
  "deck-radar-status": (payload?: DeckRadarStatusRequestDto) => void
  "deck-challenge-send": (payload: DeckChallengeSendDto) => void
  "deck-challenge-accept": (payload: {
    challengeId: string
    cardIds: string[]
  }) => void
  "deck-challenge-decline": (payload: { challengeId: string }) => void
  "deck-challenge-cancel": (payload: { challengeId: string }) => void
  "deck-play-card": (payload: {
    battleId: string
    cardId: string
    abilityId?: string | null
  }) => void
  /** Запросить снимок активного боя после reconnect (ack: { battle }). */
  "deck-battle-restore": () => void
  "arena-enter": (payload: ArenaEnterDto) => void
  "arena-leave": () => void
  "arena-queue-join": () => void
  "arena-queue-cancel": () => void
  "arena-claim-slot": (payload: ArenaClaimSlotDto) => void
  "arena-select-card": (payload: ArenaSelectCardDto) => void
  "arena-forfeit": (payload: ArenaForfeitDto) => void
  "arena-match-snapshot": (payload: ArenaMatchIdDto) => void
  "arena-status": () => void
  "arena-my-cards": (payload: ArenaMatchIdDto) => void
}

type BattleListeners = {
  connected: (payload: GameBattleConnectedPayload) => void
  "online-count-updated": (payload: OnlineCountPayload) => void
  connect_error: (payload: GameBattleErrorPayload) => void
  error: (payload: GameBattleErrorPayload) => void
  "online-players": (payload: OnlinePlayersResponse) => void
  "challenge-sent": (payload: ChallengeSentPayload) => void
  "challenge-received": (payload: ChallengeReceivedPayload) => void
  "challenge-declined": (payload: ChallengeStatusPayload) => void
  "challenge-cancelled": (payload: ChallengeStatusPayload) => void
  "challenge-expired": (payload: ChallengeStatusPayload) => void
  "radar-status": (payload: RadarStatus) => void
  "radar-queued": (payload: RadarStatus) => void
  "radar-cancelled": () => void
  "radar-queue-expired": (payload: { message: string }) => void
  "qr-room-created": (payload: { battleId: string; roomCode: string }) => void
  "qr-room-expired": (payload: {
    battleId: string
    roomCode?: string
    message: string
  }) => void
  "battle-started": (payload: BattleStartedPayload) => void
  "card-placed": (payload: CardPlacedPayload) => void
  "opponent-selected": (payload: {
    battleId: string
    opponentReady: boolean
  }) => void
  "battle-result": (payload: BattleResultPayload) => void
  "battle-cancelled": (payload: BattleCancelledPayload) => void
  "deck-radar-status": (payload: DeckRadarStatus) => void
  "deck-radar-queued": (payload: DeckRadarStatus) => void
  "deck-radar-cancelled": () => void
  "deck-battle-started": (payload: DeckBattleStartedPayload) => void
  "deck-challenge-sent": (payload: DeckChallengeSentPayload) => void
  "deck-challenge-received": (payload: DeckChallengeReceivedPayload) => void
  "deck-challenge-declined": (payload: { challengeId: string; message: string }) => void
  "deck-challenge-cancelled": (payload: { challengeId: string; message: string }) => void
  "deck-play-card-waiting": (payload: DeckPlayCardWaitingPayload) => void
  "deck-opponent-card-ready": (payload: DeckOpponentCardReadyPayload) => void
  "deck-round-result": (payload: DeckRoundResultPayload) => void
  "deck-match-result": (payload: DeckMatchResultPayload) => void
  "deck-battle-cancelled": (payload: DeckBattleCancelledPayload) => void
  "deck-battle-resume": (payload: DeckBattleResumePayload) => void
  "deck-opponent-disconnected": (payload: DeckOpponentDisconnectedPayload) => void
  "deck-opponent-reconnected": (payload: DeckOpponentReconnectedPayload) => void
  "arena-entered": (payload: ArenaEnteredPayload) => void
  "arena-online-updated": (payload: ArenaOnlineUpdatedPayload) => void
  "arena-queued": (payload: ArenaQueuedPayload) => void
  "arena-queue-cancelled": () => void
  "arena-capacity-wait": (payload: ArenaCapacityWaitPayload) => void
  "arena-match-found": (payload: ArenaMatchFoundPayload) => void
  "arena-match-event": (payload: ArenaMatchEvent) => void
  "arena-match-snapshot": (payload: ArenaMatchSnapshot) => void
  "arena-match-finished": (payload: ArenaMatchFinishedPayload) => void
  "arena-status": (payload: ArenaStatusPayload) => void
  "arena-my-cards": (payload: ArenaMatchCardsPayload) => void
}

export type BattlePresenceState = {
  /** Сервер зарегистрировал presence (событие connected) */
  ready: boolean
  onlineCount: number | null
}

type PresenceListener = (state: BattlePresenceState) => void

type RegisteredListener = {
  event: keyof BattleListeners
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (...args: any[]) => void
}

let socket: Socket | null = null
let pendingBattleStarted: BattleStartedPayload | null = null
let pendingDeckBattleStarted: DeckBattleStartedPayload | null = null
let pendingDeckBattleResume: DeckBattleResumePayload | null = null
let battleStartedBridgeAttached = false
let deckBattleStartedBridgeAttached = false
let deckBattleResumeBridgeAttached = false
let presenceBridgeAttached = false
let lastAuthToken: string | null = null
/** Один in-flight connect — иначе GuardiansOnline + BattleChallenge гоняются и ломают listeners */
let connectPromise: Promise<Socket> | null = null
let connectPromiseToken: string | null = null

const registeredListeners: RegisteredListener[] = []

let presenceState: BattlePresenceState = {
  ready: false,
  onlineCount: null,
}

const presenceListeners = new Set<PresenceListener>()

/** Ждём серверное connected (presence), не только transport connect */
const DEFAULT_PRESENCE_WAIT_MS = 12_000

function normalizeAuthToken(raw: string | null | undefined): string {
  return raw?.replace(/^Bearer\s+/i, "").trim() ?? ""
}

function emitPresence() {
  const snapshot = { ...presenceState }
  presenceListeners.forEach((listener) => listener(snapshot))
}

function resetPresenceState() {
  presenceState = { ready: false, onlineCount: null }
  emitPresence()
}

function bindListener(sock: Socket, entry: RegisteredListener) {
  const s = sock as Socket & { on: (ev: string, fn: (...args: unknown[]) => void) => void; off: (ev: string, fn: (...args: unknown[]) => void) => void }
  s.off(entry.event, entry.handler)
  s.on(entry.event, entry.handler)
}

function bindAllRegisteredListeners(sock: Socket) {
  for (const entry of registeredListeners) {
    bindListener(sock, entry)
  }
}

function attachPresenceBridge(sock: Socket) {
  if (presenceBridgeAttached) return
  presenceBridgeAttached = true

  sock.on("connected", (payload: GameBattleConnectedPayload) => {
    presenceState.ready = true
    if (payload.onlineCount != null) {
      presenceState.onlineCount = payload.onlineCount
    }
    emitPresence()
  })

  sock.on("online-count-updated", (payload: OnlineCountPayload) => {
    if (payload.onlineCount != null) {
      presenceState.onlineCount = payload.onlineCount
    }
    emitPresence()
  })

  sock.on("disconnect", () => {
    presenceState.ready = false
    emitPresence()
  })
}

function attachBattleStartedBridge(sock: Socket) {
  if (battleStartedBridgeAttached) return
  battleStartedBridgeAttached = true
  sock.on("battle-started", (payload: BattleStartedPayload) => {
    pendingBattleStarted = payload
  })
}

function attachDeckBattleStartedBridge(sock: Socket) {
  if (deckBattleStartedBridgeAttached) return
  deckBattleStartedBridgeAttached = true
  sock.on("deck-battle-started", (payload: DeckBattleStartedPayload) => {
    pendingDeckBattleStarted = payload
  })
}

function attachDeckBattleResumeBridge(sock: Socket) {
  if (deckBattleResumeBridgeAttached) return
  deckBattleResumeBridgeAttached = true
  sock.on("deck-battle-resume", (payload: DeckBattleResumePayload) => {
    pendingDeckBattleResume = payload
  })
}

function createBattleSocket(token: string): Socket {
  const sock = io(`${API_BASE}/game/battle`, {
    auth: { token },
    transports: ["websocket", "polling"],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })

  attachBattleStartedBridge(sock)
  attachDeckBattleStartedBridge(sock)
  attachDeckBattleResumeBridge(sock)
  attachPresenceBridge(sock)
  bindAllRegisteredListeners(sock)

  return sock
}

export function getBattlePresenceState(): BattlePresenceState {
  return { ...presenceState }
}

export function subscribeBattlePresence(listener: PresenceListener): () => void {
  presenceListeners.add(listener)
  listener({ ...presenceState })
  return () => {
    presenceListeners.delete(listener)
  }
}

export function consumePendingDeckBattleStarted(): DeckBattleStartedPayload | null {
  const payload = pendingDeckBattleStarted
  pendingDeckBattleStarted = null
  return payload
}

export function clearPendingDeckBattleStarted() {
  pendingDeckBattleStarted = null
}

export function consumePendingDeckBattleResume(): DeckBattleResumePayload | null {
  const payload = pendingDeckBattleResume
  pendingDeckBattleResume = null
  return payload
}

export function peekPendingDeckBattleResume(): DeckBattleResumePayload | null {
  return pendingDeckBattleResume
}

export function clearPendingDeckBattleResume() {
  pendingDeckBattleResume = null
}

export function consumePendingBattleStarted(): BattleStartedPayload | null {
  const payload = pendingBattleStarted
  pendingBattleStarted = null
  return payload
}

export function clearPendingBattleStarted() {
  pendingBattleStarted = null
}

function clearConnectPromise(token: string) {
  if (connectPromiseToken === token) {
    connectPromise = null
    connectPromiseToken = null
  }
}

function teardownSocket() {
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
  battleStartedBridgeAttached = false
  deckBattleStartedBridgeAttached = false
  deckBattleResumeBridgeAttached = false
  presenceBridgeAttached = false
  resetPresenceState()
}

function waitForSocketConnect(sock: Socket): Promise<Socket> {
  if (sock.connected) return Promise.resolve(sock)

  return new Promise((resolve, reject) => {
    const onConnect = () => {
      cleanup()
      resolve(sock)
    }
    const onError = (err: Error) => {
      cleanup()
      reject(err)
    }
    const cleanup = () => {
      sock.off("connect", onConnect)
      sock.off("connect_error", onError)
    }

    sock.once("connect", onConnect)
    sock.once("connect_error", onError)
    if (!sock.connected) {
      sock.connect()
    }
  })
}

export function waitForBattlePresenceReady(
  timeoutMs = DEFAULT_PRESENCE_WAIT_MS
): Promise<boolean> {
  if (presenceState.ready && socket?.connected) {
    return Promise.resolve(true)
  }

  return new Promise((resolve) => {
    let settled = false

    const finish = (value: boolean) => {
      if (settled) return
      settled = true
      unsub()
      clearInterval(poll)
      clearTimeout(hardTimeout)
      resolve(value)
    }

    const check = () => {
      if (presenceState.ready && socket?.connected) {
        finish(true)
      }
    }

    const unsub = subscribeBattlePresence(check)
    const poll = setInterval(check, 200)
    const hardTimeout = setTimeout(() => finish(false), timeoutMs)
    check()
  })
}

async function connectBattleSocketInternal(token: string): Promise<Socket> {
  if (socket?.connected && lastAuthToken === token) {
    return socket
  }

  // Тот же токен — ждём auto-reconnect, не рвём сокет (иначе ломается первый вход)
  if (socket && lastAuthToken === token && !socket.connected) {
    return waitForSocketConnect(socket)
  }

  if (socket) {
    teardownSocket()
  }

  lastAuthToken = token
  socket = createBattleSocket(token)
  return waitForSocketConnect(socket)
}

export async function connectBattleSocket(
  explicitToken?: string | null
): Promise<Socket> {
  const stored = explicitToken ?? getAuthToken()
  const token = normalizeAuthToken(stored)

  if (!token) {
    throw new Error("JWT required for /game/battle")
  }

  if (socket?.connected && lastAuthToken === token) {
    return socket
  }

  if (connectPromise && connectPromiseToken === token) {
    return connectPromise
  }

  connectPromiseToken = token
  connectPromise = connectBattleSocketInternal(token)
    .catch((err) => {
      clearConnectPromise(token)
      throw err
    })
    .finally(() => {
      clearConnectPromise(token)
    })

  return connectPromise
}

/** Принудительное переподключение (после деплоя бэка / reload Metro). */
export async function reconnectBattleSocket(
  explicitToken?: string | null
): Promise<Socket> {
  connectPromise = null
  connectPromiseToken = null
  teardownSocket()
  lastAuthToken = null
  return connectBattleSocket(explicitToken)
}

export function getBattleSocket(): Socket | null {
  return socket
}

export function disconnectBattleSocket() {
  connectPromise = null
  connectPromiseToken = null
  teardownSocket()
  lastAuthToken = null
  pendingBattleStarted = null
  pendingDeckBattleStarted = null
  pendingDeckBattleResume = null
}

export function emitBattleEvent<K extends keyof BattleEvents>(
  event: K,
  ...args: Parameters<BattleEvents[K]>
) {
  if (!socket?.connected) {
    return false
  }
  socket.emit(event, ...args)
  return true
}

const DEFAULT_RESTORE_ACK_MS = 8_000

/** Запросить снимок боя вручную, если deck-battle-resume не пришёл. */
export async function requestDeckBattleRestore(
  explicitToken?: string | null,
  timeoutMs = DEFAULT_RESTORE_ACK_MS
): Promise<DeckBattleRestoreAck | null> {
  try {
    await connectBattleSocket(explicitToken)
  } catch {
    return null
  }

  const presenceReady = await waitForBattlePresenceReady()
  if (!presenceReady || !socket?.connected) {
    return null
  }

  const sock = socket
  return new Promise((resolve) => {
    let settled = false
    const finish = (value: DeckBattleRestoreAck | null) => {
      if (settled) return
      settled = true
      resolve(value)
    }

    const timer = setTimeout(() => finish(null), timeoutMs)

    sock
      .timeout(timeoutMs)
      .emit("deck-battle-restore", (err: Error | null, ack?: unknown) => {
        clearTimeout(timer)
        if (err) {
          finish(null)
          return
        }
        if (!ack || typeof ack !== "object" || Array.isArray(ack)) {
          finish({ battle: null })
          return
        }
        const battle = (ack as { battle?: unknown }).battle ?? null
        finish({
          battle: (battle as DeckBattleResumePayload | null) ?? null,
        })
      })
  })
}

export async function emitBattleEventWhenReady<K extends keyof BattleEvents>(
  token: string | null | undefined,
  event: K,
  ...args: Parameters<BattleEvents[K]>
): Promise<boolean> {
  try {
    await connectBattleSocket(token)
  } catch {
    return false
  }

  const presenceReady = await waitForBattlePresenceReady()
  if (!presenceReady) {
    return false
  }

  return emitBattleEvent(event, ...args)
}

export function onBattleEvent<K extends keyof BattleListeners>(
  event: K,
  handler: BattleListeners[K]
) {
  const entry: RegisteredListener = {
    event,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler: handler as (...args: any[]) => void,
  }
  registeredListeners.push(entry)

  if (socket) {
    bindListener(socket, entry)
  }

  return () => {
    const idx = registeredListeners.indexOf(entry)
    if (idx >= 0) registeredListeners.splice(idx, 1)
    ;(socket as Socket & { off: (ev: string, fn: (...args: unknown[]) => void) => void } | null)?.off(
      event,
      entry.handler
    )
  }
}
